import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Alert,
  Stack,
  Box,
  Chip,
  LinearProgress,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { createBatchId } from '../../shared/createBatchId';

import PseudoDocxDropzone from './PseudoDocxDropzone';
import { pseudoAnonymizeDocxPII } from '../../services/piiService';
import PIIAnonymizationOptionsDialog from '../PIIAnonymizerPage/PIIFileUploadPanel/PIIAnonymizationOptionsDialog/PIIAnonymizationOptionsDialog';
import PreviewWord from '../PIIAnonymizerPage/PIIFileUploadPanel/PreviewWord'; 

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'pseudonymized.docx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const statusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  if (status === 'uploading') return 'warning';
  return 'default';
};

const statusLabel = (status, t) => {
  if (status === 'success') return t('common.done', 'Hotovo');
  if (status === 'error') return t('common.error', 'Chyba');
  if (status === 'uploading') return t('common.processing', 'Spracovávam');
  return t('common.ready', 'Pripravené');
};

const PseudoFileUploadPanel = () => {
  const { t } = useTranslation();

  const [fileItem, setFileItem] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // State to handle the file being previewed
  const [previewFile, setPreviewFile] = useState(null);

  const [options, setOptions] = useState({
    save_to_db: false,
    anonymize_birth_number: false,
    anonymize_birth_date: false,
    anonymize_names: false,
    exclude_contract_info_first_page: false,
    name_confidence_threshold: 60,
    anonymize_signature: false,
    anonymize_account_number: false,
    anonymize_address: false,
    anonymize_phone: false,
    anonymize_email: false,
    anonymize_passport_number: false,
    anonymize_id_number: false,
    text_anonymization_mode: 'random',
    signature_anonymization_mode: 'black_box',
    custom_words: [],
    profileId: null,
    defaultTemplateId: null,
  });

  const [optionsOpen, setOptionsOpen] = useState(false);

  const canStart = useMemo(() => !!fileItem?.file && !isUploading, [fileItem, isUploading]);
  const canDownload = useMemo(() => fileItem?.status === 'success' && fileItem?.blob, [fileItem]);

  const resetMessages = () => {
    setError('');
    setInfoMessage('');
  };

  const handleFilesAdded = (files) => {
    resetMessages();
    const f = files?.[0];
    if (!f) return;

    setFileItem({
      id: crypto?.randomUUID?.() || String(Date.now()),
      name: f.name,
      file: f,
      status: 'ready',
      progress: 0,
      blob: null,
      downloadName: null,
      error: '',
    });
  };

  const handleRemove = () => {
    if (isUploading) return;
    setFileItem(null);
    setPreviewFile(null); // Clear preview state when file is removed
    resetMessages();
  };

  const handleOpenOptions = () => {
    if (!fileItem?.file || isUploading) return;
    setOptionsOpen(true);
  };

  const runPseudo = async (opts) => {
    if (!fileItem?.file || isUploading) return;
    const groupId = createBatchId();

    resetMessages();
    setIsUploading(true);
    setFileItem((prev) => ({
      ...prev,
      status: 'uploading',
      progress: 30,
      error: '',
      blob: null,
      downloadName: null,
    }));

    try {
      const res = await pseudoAnonymizeDocxPII(fileItem.file, {
        ...opts,
        anonymizationGroupId: groupId,
      });

      if (!res.success) {
        setFileItem((prev) => ({
          ...prev,
          status: 'error',
          progress: 100,
          error: res.error || t('common.error', 'Chyba'),
        }));
        setError(res.error || t('common.error', 'Chyba'));
        return;
      }

      const { blob, filename } = res.data || {};
      setFileItem((prev) => ({
        ...prev,
        status: 'success',
        progress: 100,
        blob,
        downloadName: filename || `pseudonymized_${prev.name}`,
      }));

      setInfoMessage(t('PseudoFileUploadPanel.success', 'Hotovo.'));
    } catch (e) {
      console.error(e);
      setFileItem((prev) => ({
        ...prev,
        status: 'error',
        progress: 100,
        error: e?.message || 'Failed',
      }));
      setError(e?.message || t('common.error', 'Chyba'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmOptions = async () => {
    setOptionsOpen(false);
    await runPseudo(options);
  };

  const handleDownload = () => {
    if (!canDownload) return;
    downloadBlob(fileItem.blob, fileItem.downloadName || fileItem.name);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        borderRadius: 3, 
        border: '1px solid', 
        borderColor: 'divider', 
        bgcolor: 'background.paper',
        height: '100%'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {t('PseudoFileUploadPanel.title', 'Pseudonymizácia .docx')}
      </Typography>

      <PseudoDocxDropzone disabled={isUploading} hasFile={!!fileItem} onFilesAdded={handleFilesAdded} />

      {fileItem && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0, mr: 1, flex: 1 }}>
              <Typography variant="body2" noWrap title={fileItem.name} sx={{ fontWeight: 500 }}>
                {fileItem.name}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={statusLabel(fileItem.status, t)} size="small" color={statusColor(fileItem.status)} sx={{ borderRadius: 1.5 }} />

              {/* Actions available only when processing is successfully completed */}
              {fileItem.status === 'success' && fileItem.blob && (
                <>
                  <IconButton size="small" onClick={() => setPreviewFile(fileItem)} sx={{ bgcolor: 'background.paper' }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  
                  <IconButton size="small" onClick={handleDownload} sx={{ bgcolor: 'background.paper' }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </>
              )}

              <IconButton size="small" onClick={handleRemove} disabled={isUploading} color="error" sx={{ bgcolor: 'background.paper' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <LinearProgress variant="determinate" value={fileItem.progress} sx={{ borderRadius: 2 }} />

          {fileItem.error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
              {fileItem.error}
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert severity="success" sx={{ mb: 1, borderRadius: 2 }}>
          {infoMessage}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          disableElevation
          disabled={!canStart} 
          onClick={handleOpenOptions}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {isUploading ? t('PseudoFileUploadPanel.processing', 'Spracovávam...') : t('PseudoFileUploadPanel.start', 'Spustiť pseudonymizáciu')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          disabled={!canDownload}
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {t('PseudoFileUploadPanel.download', 'Stiahnuť výsledok')}
        </Button>
      </Stack>

      <PIIAnonymizationOptionsDialog
        open={optionsOpen}
        options={options}
        onChangeOptions={setOptions}
        onClose={() => setOptionsOpen(false)}
        onConfirm={handleConfirmOptions}
        loading={isUploading}
        pseudoAnonymization={true}
        hideSignature={true}
      />

      {/* Document preview component */}
      {previewFile && (
        <PreviewWord 
          open={!!previewFile} 
          fileItem={previewFile} 
          onClose={() => setPreviewFile(null)} 
        />
      )}
    </Paper>
  );
};

export default PseudoFileUploadPanel;